import { Button, List, Col, Row, Space, App } from 'antd';
import React, { useState } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import SosIcon from '@/assets/icons/sos.png';
import { Card, SosDetailsModal, SosItem } from '@/components/atoms';
import { PageHeader } from '@/components/organisms';
import { Layout } from '@/components/templates';
import { Sos, SosRejectedContainerQuery, SosStatus } from '@/graphql';

type Props = {
    data?: SosRejectedContainerQuery['sosByStatus'];
    loading: boolean;
    onRefund: (id: string) => void;
};

const SosRejectedView = ({ data, loading, onRefund }: Props) => {
    const { modal } = App.useApp();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [mySos, setMySos] = useState<Array<Sos>>([]);

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleRefund = (id: string) => {
        modal.confirm({
            title: 'Refund completed?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    Please confirm that you have processed the refund for this SOS request from the stripe dashboard.
                    <br />
                    <br />
                    <small>
                        <i>Please visit transaction page for more details, then click on the refund button. (View payment) link will redirect you to the transaction page.</i>
                    </small>
                </div>
            ),
            okText: 'Proceed',
            cancelButtonProps: {
                danger: true,
            },
            onOk() {
                onRefund(id);
            },
        });
    };

    const getRefundRequestedActionButtons = (sos: any) => {
        const buttons = [];
        if (sos.isItemPaid) {
            buttons.push(
                <Button
                    onClick={() => {
                        if (sos.paymentId) {
                            handleRefund(sos.id);
                        }
                    }}
                    style={{ backgroundColor: 'red' }}
                    type="primary"
                >
                    Finalize Refund
                </Button>
            );
        }

        if (sos && sos.user && sos.user.mySos) {
            buttons.push(
                <Button
                    onClick={() => {
                        if (sos?.user?.mySos) {
                            setMySos(sos.user.mySos as unknown as Array<Sos>);
                        }
                        showModal();
                    }}
                    type="link"
                >
                    SOS History ({sos.user.mySos.length})
                </Button>
            );
        }
        return buttons;
    };

    const getActionButtons = (sos: any) => {
        const buttons = [];

        if (sos && sos.user && sos.user.mySos) {
            buttons.push(
                <Button
                    onClick={() => {
                        if (sos?.user?.mySos) {
                            setMySos(sos.user.mySos as unknown as Array<Sos>);
                        }
                        showModal();
                    }}
                    type="link"
                >
                    SOS History ({sos.user.mySos.length})
                </Button>
            );
        }
        return buttons;
    };

    return (
        <>
            <PageHeader icon={{ src: SosIcon }} title="Rejected SOS Requests" />
            <Layout>
                <Row gutter={[0, 32]}>
                    <Col span={24}>
                        <Card style={{ marginBottom: 16 }} title={<Space>{/* {icon} {title} */} Refund Requested</Space>}>
                            <List
                                dataSource={data?.filter((sos) => sos.status === SosStatus.RefundRequested)}
                                itemLayout="vertical"
                                loading={loading}
                                locale={{
                                    emptyText: 'There are no refund requested SOS requests',
                                }}
                                renderItem={(sos) => {
                                    return <SosItem {...sos} actions={getRefundRequestedActionButtons(sos)} />;
                                }}
                                size="large"
                            />
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[0, 32]}>
                    <Col span={24}>
                        <Card style={{ marginBottom: 16 }} title={<Space>Refunded</Space>}>
                            <List
                                dataSource={data?.filter((sos) => sos.status === SosStatus.Refunded)}
                                itemLayout="vertical"
                                loading={loading}
                                locale={{
                                    emptyText: 'There are no refunded SOS requests',
                                }}
                                renderItem={(sos) => {
                                    return <SosItem {...sos} actions={getActionButtons(sos)} />;
                                }}
                                size="large"
                            />
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[0, 32]}>
                    <Col span={24}>
                        <Card style={{ marginBottom: 16 }} title={<Space>Rejected</Space>}>
                            <List
                                dataSource={data?.filter((sos) => sos.status === SosStatus.Rejected)}
                                itemLayout="vertical"
                                loading={loading}
                                locale={{
                                    emptyText: 'There are no rejected SOS requests',
                                }}
                                renderItem={(sos) => {
                                    return <SosItem {...sos} actions={getActionButtons(sos)} />;
                                }}
                                size="large"
                            />
                        </Card>
                    </Col>
                </Row>

                <SosDetailsModal handleCancel={handleCancel} isModalVisible={isModalVisible} messages={mySos} page="pending" />
            </Layout>
        </>
    );
};

export { SosRejectedView };
